/**
 * Variant A client-side normalize (staging/allowlist path only — callers gate).
 *
 * Pipeline per original photo, BEFORE upload:
 *   decode (EXIF-upright) -> downscale to long-edge cap -> JPEG encode
 * so the wire carries a small upright JPEG and the API can finalize
 * metadata-only (no server Sharp).
 *
 * Tiers:
 *   1. Web Worker (OffscreenCanvas + createImageBitmap) — normal path
 *   2. heic2any (lazy WASM import) for HEIC files the browser can't decode
 *      natively (Chromium/Windows), then back through tier 1's encoder
 *   3. Main-thread <canvas> when OffscreenCanvas is unavailable (old Safari)
 *   4. Give up -> caller uploads the raw file (server Sharp fallback; real
 *      HEIC will fail there with "try a JPG" — prod Sharp has no HEVC).
 *
 * Tuning knobs (plan: pre-agreed fallback q0.85 / 2200px if durable
 * completion overshoots 15s in Step 3 QA):
 */
export const NORMALIZE_MAX_LONG_EDGE =
  Number(process.env.REACT_APP_NORMALIZE_MAX_LONG_EDGE) || 2625;
export const NORMALIZE_JPEG_QUALITY =
  Number(process.env.REACT_APP_NORMALIZE_JPEG_QUALITY) || 0.92;

/** Cap simultaneous decodes; does not replace per-job bitmap release. */
const NORMALIZE_CONCURRENCY = 2;

let worker = null;
let jobSeq = 0;
const pending = new Map();

function getWorker() {
  if (worker) return worker;
  worker = new Worker(new URL("../util/imageNormalizeWorker.js", import.meta.url));
  worker.onmessage = (event) => {
    const { id } = event.data || {};
    const job = pending.get(id);
    if (!job) return;
    pending.delete(id);
    job.resolve(event.data);
  };
  worker.onerror = (err) => {
    // Fail every in-flight job; callers fall back to raw upload.
    for (const [, job] of pending) {
      job.resolve({ ok: false, error: String(err?.message || "worker error") });
    }
    pending.clear();
    try { worker.terminate(); } catch (_) { /* noop */ }
    worker = null;
  };
  return worker;
}

function runWorkerJob(buffer, type) {
  return new Promise((resolve) => {
    const id = ++jobSeq;
    pending.set(id, { resolve });
    getWorker().postMessage(
      {
        id,
        buffer,
        type,
        maxLongEdge: NORMALIZE_MAX_LONG_EDGE,
        quality: NORMALIZE_JPEG_QUALITY,
      },
      [buffer],
    );
  });
}

function supportsWorkerPipeline() {
  return (
    typeof Worker !== "undefined" &&
    typeof OffscreenCanvas !== "undefined" &&
    typeof createImageBitmap !== "undefined"
  );
}

function isHeicLike(file) {
  const type = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  return (
    type.includes("heic") ||
    type.includes("heif") ||
    name.endsWith(".heic") ||
    name.endsWith(".heif")
  );
}

/** Tier 2: HEIC -> JPEG via lazy-loaded WASM, for browsers without native decode. */
async function convertHeicToJpegBlob(file) {
  const { default: heic2any } = await import("heic2any");
  const result = await heic2any({
    blob: file,
    toType: "image/jpeg",
    quality: NORMALIZE_JPEG_QUALITY,
  });
  return Array.isArray(result) ? result[0] : result;
}

/** Tier 3: main-thread canvas for browsers without OffscreenCanvas. */
async function normalizeOnMainThread(blob) {
  const bitmap = await createImageBitmap(blob, { imageOrientation: "from-image" });
  try {
    const scale = Math.min(
      1,
      NORMALIZE_MAX_LONG_EDGE / Math.max(bitmap.width, bitmap.height),
    );
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, width, height);
    return await new Promise((resolve, reject) => {
      canvas.toBlob(
        (out) => (out ? resolve(out) : reject(new Error("canvas.toBlob returned null"))),
        "image/jpeg",
        NORMALIZE_JPEG_QUALITY,
      );
    });
  } finally {
    if (bitmap.close) bitmap.close();
  }
}

function createLimiter(maxConcurrent) {
  let active = 0;
  const queue = [];
  const pump = () => {
    if (active >= maxConcurrent || queue.length === 0) return;
    active += 1;
    const { fn, resolve, reject } = queue.shift();
    Promise.resolve()
      .then(fn)
      .then(resolve, reject)
      .finally(() => {
        active -= 1;
        pump();
      });
  };
  return (fn) =>
    new Promise((resolve, reject) => {
      queue.push({ fn, resolve, reject });
      pump();
    });
}

const normalizeLimiter = createLimiter(NORMALIZE_CONCURRENCY);

async function normalizeBlobOnce(blob, type) {
  if (supportsWorkerPipeline()) {
    const buffer = await blob.arrayBuffer();
    const result = await runWorkerJob(buffer, type);
    if (result.ok) {
      return new Blob([result.buffer], { type: "image/jpeg" });
    }
    throw new Error(result.error || "worker normalize failed");
  }
  if (typeof createImageBitmap !== "undefined") {
    return normalizeOnMainThread(blob);
  }
  throw new Error("no client decode capability");
}

/**
 * Normalize one original photo for upload.
 *
 * @returns {Promise<{
 *   file: File,             // what to upload (normalized JPEG, or the input on failure)
 *   clientNormalized: boolean,
 *   bytes: number,          // pre-normalize size
 *   normalized_bytes: number|null,
 *   normalize_start: number|null,
 *   normalize_end: number|null,
 * }>}
 * Never throws — failure returns the original file with clientNormalized:false.
 */
export async function normalizeOriginalForUpload(file) {
  const bytes = file.size;
  const jpegName = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";

  return normalizeLimiter(async () => {
    const normalizeStart = Date.now();
    try {
      let outBlob;
      try {
        outBlob = await normalizeBlobOnce(file, file.type);
      } catch (firstErr) {
        if (!isHeicLike(file)) throw firstErr;
        // HEIC the browser couldn't decode natively — WASM tier, then encode.
        const jpegBlob = await convertHeicToJpegBlob(file);
        outBlob = await normalizeBlobOnce(jpegBlob, "image/jpeg");
      }

      const outFile = new File([outBlob], jpegName, { type: "image/jpeg" });
      return {
        file: outFile,
        clientNormalized: true,
        bytes,
        normalized_bytes: outFile.size,
        normalize_start: normalizeStart,
        normalize_end: Date.now(),
      };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        `[STAGING-NORMALIZE] fallback to raw upload for ${file.name}: ${err?.message || err}`,
      );
      return {
        file,
        clientNormalized: false,
        bytes,
        normalized_bytes: null,
        normalize_start: normalizeStart,
        normalize_end: Date.now(),
      };
    }
  });
}
